
import {
  ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnInit, Output
} from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { from as observableFrom, of as observableOf } from 'rxjs';
import { catchError, combineAll, map } from 'rxjs/operators';

import {
  EntityConnectionTypeDetails, GetEntityConnectionTypesGQL, GetEntityPublicGQL
} from 'generated/graphql';

import {
  ConnectionMatchingOperatorDialog
} from 'app/query-common/dialogs/connection-matching-operator/connection-matching-operator.dialog';
import {
  DateRangeUsersDialogCloseData, DateRangeUsersOperatorDialog
} from 'app/query-common/dialogs/date-range-users-operator/date-range-users-operator.dialog';
import { isNilOrEmpty } from 'helpers';
import {
  getDefaultTagCategoryResponsesRuleSet, getDefaultTagsComboRule
} from 'query-builder/core/simple-query-default-ruleset';
import { Field, RuleSet } from 'query-builder/query.interfaces';
import {
  RangeOrRelativeDateInputModel
} from 'shared/components/range-or-relative-date-input/range-or-relative-date-input.component';

import { EntityConnectionSubqueryHelpers } from '../../services/EntityConnectionSubqueryHelpers';
import {
  IEntityConnectionFieldValueViewModel, IEntityConnectionSubqueryQueryField
} from './EntityConnectionFieldViewModel';

@Component({
  selector: 'app-entity-connection-subquery-input',
  templateUrl: './entity-connection-subquery-input.component.html',
  styleUrls: ['./entity-connection-subquery-input.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => EntityConnectionSubqueryInputComponent), multi: true }
  ]
})
export class EntityConnectionSubqueryInputComponent
  implements ControlValueAccessor, OnInit {
  /* Required */
  @Input() campaignId: string;
  @Input() isSimpleQueryFormat: boolean;
  /** Field configuration (not used)*/
  @Input() field: Field;
  /** Disabled state of the input (not implemented)*/
  @Input() disabled: boolean;

  /**
   * This is used in the simple query format
   * to switch the operator between 'include' and 'exclude'
   * It's not used in advanced query builder as this
   * is handled by the operator dropdown.
   */
  @Input() operator: string;
  @Output() operatorChange = new EventEmitter<string>();

  entityConnectionTypes: EntityConnectionTypeDetails.Fragment[] = [];
    /** Entity models are retrieved async based upon the entity ids. */
  selectedEntities = [];

  uiTagsOrCategories: 'tag_category' | 'tags' = 'tags';
  uiTagsOrCategoriesLabels: Record<'tag_category' | 'tags', string> = {
    tags: 'Info (Responses)',
    tag_category: 'Info (Fields)'
  };

  // <Internal query variables>
  hasConnections: boolean;
  entityConnectionTypeId: string;
  connectionMatchingRule: string;
  tagsComboRuleSet: RuleSet;
  tagCategoryResponsesRuleSet: RuleSet;
  include_inactive: boolean;
  createdBy: string[] = [];
  createdDateRange: RangeOrRelativeDateInputModel;
  private entityIds = [];
  // </Internal query variables>

  // For ControlValueAccessor interface
  public onChangeCallback: () => void;
  public onTouchedCallback: () => any;

  @Input()
  get value(): IEntityConnectionSubqueryQueryField['value'] {
    const internalValue: IEntityConnectionFieldValueViewModel = {
      uiTagsOrCategories: this.uiTagsOrCategories,
      tagsComboRuleSet: this.tagsComboRuleSet,
      tagCategoryResponsesRuleSet: this.tagCategoryResponsesRuleSet,
      entityIds: this.entityIds,
      include_inactive: !!this.include_inactive,
      createdBy: this.createdBy,
      createdDateRange: this.createdDateRange,
      hasConnections: this.hasConnections,
      entityConnectionTypeId: this.entityConnectionTypeId
    };

    const result = EntityConnectionSubqueryHelpers.toComplexConnectionsRuleFormValue(internalValue);
    return result;
  }

  set value(value: IEntityConnectionSubqueryQueryField['value']) {
    const simpleUIModel = EntityConnectionSubqueryHelpers.toSimpleConnectionsUIValue(value);

    // TODO: Set the filterConnectionsBy based on the values here when loading
    // a saved query. This is not implemented yet.
    this.tagCategoryResponsesRuleSet = simpleUIModel.tagCategoryResponsesRuleSet;
    this.tagsComboRuleSet = simpleUIModel.tagsComboRuleSet;
    this.uiTagsOrCategories = simpleUIModel.uiTagsOrCategories || 'tags';
    this.hasConnections = simpleUIModel.hasConnections;
    this.entityConnectionTypeId = simpleUIModel.entityConnectionTypeId;
    this.entityIds = simpleUIModel.entityIds;
    this.include_inactive = simpleUIModel.include_inactive;
    this.createdDateRange = simpleUIModel.createdDateRange;
    this.createdBy = simpleUIModel.createdBy;
    this.loadEntities(this.entityIds);
    this.handleDataChange();
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _getEntityPublicGQL: GetEntityPublicGQL,
    private _matDialog: MatDialog,
    private _getEntityConnectionTypesGQL: GetEntityConnectionTypesGQL
  ) {
  }

  ngOnInit(): void {
    if (!this.campaignId) {
      throw new Error('campaignId is required for entity-connection-subquery-input');
    }

    this._getEntityConnectionTypesGQL
    .fetch({
      campaignId: this.campaignId
    }).pipe(
      map(result => result.data.getEntityConnectionTypes)
    ).subscribe({
      next: (types) => {
       this.entityConnectionTypes = types;
      }
    });

  }

  onEntityRemoved(entity: { id: string }) {
    this.selectedEntities = (this.selectedEntities || []).filter(i => i.id !== entity.id);
    this.entityIds = this.selectedEntities.map(i => i.id);
    this.handleDataChange();
  }

  includeInactiveChkChanged(event: MatCheckboxChange) {
    this.include_inactive = event.checked;
    this.handleDataChange();
  }

  dateRangeAndUsersClicked(_event: DateRangeUsersDialogCloseData) {
    const dialogRef = DateRangeUsersOperatorDialog.open(this._matDialog, {
      data: {
        campaignId: this.campaignId,
        title: `Entity Connections`,
        dateRange: this.createdDateRange,
        userIds: this.createdBy,
        showOperators: false,
        forTypeText: 'connections',
        targetType: 'EntitConnectionType'
      }
    });

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          this.createdDateRangeChanged(result.dateRange);
          this.usersChanged(result.userIds);
        }
      });
  }

  createdDateRangeChanged(event: RangeOrRelativeDateInputModel) {
    this.createdDateRange = event;
    this.handleDataChange();
  }

  usersChanged(event: string[]) {
    this.createdBy = event;
    this.handleDataChange();
  }

  operatorChanged(event: string) {
    if (event) {
      this.operator = event;
      this.operatorChange.emit(event);
      this.handleDataChange();
    }
  }

  onEntitySelected(entity: { id: string }) {
    if ((this.selectedEntities || []).map(i => i.id).includes(entity.id)) {
      return;
    }
    this.selectedEntities = [...this.selectedEntities, entity];
    this.entityIds = (this.selectedEntities || []).map(i => i.id);
    this.handleDataChange();
  }

  connectionMatchingButtonClicked() {
    const dialogRef = ConnectionMatchingOperatorDialog.open(this._matDialog, {
      data: {
        campaignId: this.campaignId,
        hasConnections: !!this.hasConnections,
        entityConnectionTypeId: this.entityConnectionTypeId,
        entityConnectionTypes: this.entityConnectionTypes
      }
    });

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          this.hasConnections = result.hasConnections;
          this.entityConnectionTypeId = result.entityConnectionTypeId;
          this.handleDataChange();
        }
      });
  }

  getConnectionMatchingText() {
    const matchingText = this.hasConnections ? 'any' : 'matching';
    let connectionText  = '';
    if(this.entityConnectionTypeId) {
      const ect = this.entityConnectionTypes.find(type => type.id === this.entityConnectionTypeId);
      const entityType1Name = ect?.entityType1?.namePlural;
      const entityType2Name = ect?.entityType2?.namePlural;
      connectionText = ' “' + entityType1Name + ' + ' + entityType2Name + '” ';
    }
    return matchingText + connectionText + ' connections';
  }

  toggleFilterOnTagCategoryOrTags() {
    const a = this.uiTagsOrCategories === 'tags' ? 'responses' : 'fields';
    const b = this.uiTagsOrCategories === 'tags' ? 'fields' : 'responses';
    const proceed = confirm(`Switching from ${a} to ${b} will remove your selected fields. Are you sure you want to continue?`);
    if (!proceed) {
      return;
    }

    if (this.uiTagsOrCategories === 'tags') {
      this.uiTagsOrCategories = 'tag_category';
      this.tagsComboRuleSet = getDefaultTagsComboRule('entity_connection');
    } else {
      this.uiTagsOrCategories = 'tags';
      this.tagCategoryResponsesRuleSet = getDefaultTagCategoryResponsesRuleSet('entity_connection');
    }

    this.handleDataChange();
  }
  // ----------Validator Implementation----------
  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }

  // ----------ControlValueAccessor Implementation----------


  writeValue(obj: IEntityConnectionSubqueryQueryField['value']): void {
    if (obj) {
      this.value = obj;
    } else { }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = () => fn(this.value);
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = () => fn(this.value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ----------END----------

  private loadEntities(entityIds: string[]) {
    if (isNilOrEmpty(entityIds)) {
      this.selectedEntities = [];
      return;
    }

    observableFrom(entityIds || []).pipe(
      map(entityId => this._getEntityPublicGQL.fetch({
        entityId,
        campaignId: this.campaignId
      }).pipe(
        catchError(e => observableOf(null)),
        map(res => res ? res.data.getEntityPublic : null)
      )),
      combineAll()
    )
      .subscribe({
        next: (results) => {
          // Ignore null responses
          const entities = results.filter(r => !!r);
          this.selectedEntities = entities;
        },
        error: (err) => {
          console.warn(err);
        },
        complete: () => this.handleDataChange()
      });
  }

  private handleDataChange(): void {
    this._cd.markForCheck();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }
}
